import React, { useState, useEffect } from "react";
import {
  Modal,
  Stack,
  Text,
  Button,
  Alert,
  Group,
  Checkbox,
  Divider,
} from "@mantine/core";
import { notifyError } from "../../utils/notify";
import { IconTrash, IconAlertTriangle, IconCheck } from "@tabler/icons-react";
import { useApp } from "../../context/AppContext";
import { useResponsive } from "../../context/ResponsiveContext";
import {
  PackageReasonCategory,
  REVOKE_REASON_CATEGORIES,
} from "../../types/packages";
import ReasonFields from "./ReasonFields";
import BrandedLoadingOverlay from "../common/BrandedLoadingOverlay";

interface RevokePackageModalProps {
  opened: boolean;
  onClose: () => void;
  salonId: string;
  /** The owned package (client_service_package) id. */
  cspId: string;
  packageName: string;
  /** Total remaining units across the package's service banks (all forfeited). */
  remainingUnits: number;
  onSuccess: () => void | Promise<void>;
}

/**
 * Revoke a client's owned package. DESTRUCTIVE and irreversible: every remaining
 * unit is forfeited and the package transitions to `revoked`. Requires an
 * explicit confirmation checkbox in addition to a reason + note, and records a
 * `revoke_package` audit row.
 */
export default function RevokePackageModal({
  opened,
  onClose,
  salonId,
  cspId,
  packageName,
  remainingUnits,
  onSuccess,
}: RevokePackageModalProps) {
  const { isMobile } = useResponsive();
  const { revokeClientPackage } = useApp();

  const [confirmed, setConfirmed] = useState(false);
  const [category, setCategory] = useState<PackageReasonCategory | null>(null);
  const [note, setNote] = useState("");
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    if (opened) {
      setConfirmed(false);
      setCategory(null);
      setNote("");
      setError(null);
      setIsSubmitting(false);
    }
  }, [opened]);

  const canSubmit = confirmed && !isSubmitting;

  const handleSubmit = async () => {
    if (!canSubmit) return;
    setIsSubmitting(true);
    setError(null);
    try {
      await revokeClientPackage(salonId, cspId, {
        reason_category: category,
        note: note.trim(),
      });
      notifyError(`${packageName} has been revoked.`, {
        title: "Package revoked",
        icon: <IconCheck size={16} />,
      });
      try {
        await Promise.resolve(onSuccess());
      } catch (e) {
        console.error("Error in revoke success handler:", e);
      }
      onClose();
    } catch (err) {
      console.error("Error revoking package:", err);
      setError(
        err instanceof Error
          ? err.message
          : "Failed to revoke the package. Please try again.",
      );
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <Modal
      opened={opened}
      onClose={onClose}
      title={<Text fw={600}>Revoke Package</Text>}
      size="md"
      fullScreen={isMobile}
      centered
      closeOnClickOutside={!isSubmitting}
      closeOnEscape={!isSubmitting}
    >
      <BrandedLoadingOverlay visible={isSubmitting} />

      <Stack gap="md">
        {error && (
          <Alert icon={<IconAlertTriangle size={16} />} color="red">
            {error}
          </Alert>
        )}

        <ReasonFields
          category={category}
          note={note}
          onCategoryChange={setCategory}
          onNoteChange={setNote}
          disabled={isSubmitting}
          categories={REVOKE_REASON_CATEGORIES}
          categoryRequired={false}
          noteRequired={false}
        />

        <Checkbox
          color="red"
          checked={confirmed}
          onChange={(e) => setConfirmed(e.currentTarget.checked)}
          disabled={isSubmitting}
          label={`I understand all ${remainingUnits} remaining ${
            remainingUnits === 1 ? "unit" : "units"
          } in ${packageName} will be forfeited and this cannot be undone.`}
        />

        <Divider />

        <Group justify="flex-end">
          <Button variant="default" onClick={onClose} disabled={isSubmitting}>
            Cancel
          </Button>
          <Button
            color="red"
            onClick={handleSubmit}
            loading={isSubmitting}
            disabled={!canSubmit}
            leftSection={<IconTrash size={18} />}
          >
            Revoke Package
          </Button>
        </Group>
      </Stack>
    </Modal>
  );
}
